// Bunch of types to create type-safe overrides. If you add an extra brand or
// language the compiler should tell you where you need to make changes.

export type Brand<T> = {
  money101: T;
  moneyhero: T;
  singsaver: T;
  moneymax: T;
};

export type BrandId = keyof Brand<unknown>;

type LocaleEN<T> = {
  'en-sg': T;
  'en-hk': T;
  'en-ph': T;
};
// Either provide 'en' or require all individual English locales
type FallbackEN<T> = LocaleEN<T> | ({ en: T } & Partial<LocaleEN<T>>);

type LocaleZH<T> = {
  'zh-hk': T;
  'zh-tw': T;
};
// Either provide 'zh' or require all individual Chinese locales
type FallbackZH<T> = LocaleZH<T> | ({ zh: T } & Partial<LocaleZH<T>>);

// Locales without fallback
type Locale<T> = FallbackEN<T> & FallbackZH<T>;

// Require all keys, or allow partial entries when a default is specified
type Default<T, K> = T | ({ _: K } & Partial<T>);

export type BrandSpecific<T> = Default<Brand<T>, T>;
export type LocaleSpecific<T> = Default<Locale<T>, T>;

export type LocaleSpecificGroup<T = unknown> = {
  [key in keyof T]: LocaleSpecific<T[key]>;
};

export type BrandSpecificGroup<T = unknown> = {
  [key in keyof T]: BrandSpecific<T[key]>;
};

export type StaticTextOverride = LocaleSpecificGroup<Record<string, string>>;

function resolveBrand<T>(id: string, obj: BrandSpecific<T>): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (obj as any)[id] ?? (obj as any)._;
}

function resolveLocale<T>(locale: string, obj: LocaleSpecific<T>): T {
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (obj as any)[locale] ?? (obj as any)[locale.slice(0, 2)] ?? (obj as any)._
  );
}

export type Resolvable<T> = { get(t: string): T };

/**
 * Retrieve a value for a specific brand
 *
 * Takes an object with keys for various brands. You can use either brand id
 * ("singsaver"), or a default value ("_").
 */
export function brandSpecific<T>(template: BrandSpecific<T>): Resolvable<T> {
  return {
    get: (id) => resolveBrand(id, template),
  };
}

/**
 * Create an object with an entry for every brand.
 */
export function brand<T>(template: BrandSpecific<T>): Brand<T> {
  return {
    money101: resolveBrand('money101', template),
    moneyhero: resolveBrand('moneyhero', template),
    singsaver: resolveBrand('singsaver', template),
    moneymax: resolveBrand('moneymax', template),
  };
}

/**
 * Retrieves multiple values for a specific brand
 *
 * Takes an object with keys for various brands. You can use either brand id
 * ("singsaver"), or a default value ("_").
 */
export function brandSpecificGroup<T>(
  templates: BrandSpecificGroup<T>,
): Resolvable<T> {
  return {
    get(id) {
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(templates)) {
        result[key] = resolveBrand(id, value as BrandSpecific<unknown>);
      }
      return result as T;
    },
  };
}

/**
 * Retrieve a value for a specific locale
 *
 * Takes an object with keys for various locales. You can use either the full
 * locale ("en-sg"), language ("en"), or a default value ("_").
 */
export function localeSpecific<T>(template: LocaleSpecific<T>): Resolvable<T> {
  return {
    get: (locale) => resolveLocale(locale, template),
  };
}

/**
 * Retrieve multiple values for a specific locale
 *
 * Takes an object with keys for various locales. You can use either the full
 * locale ("en-sg"), language ("en"), or a default value ("_").
 */
export function localeSpecificGroup<T = unknown>(
  templates: LocaleSpecificGroup<T>,
): Resolvable<T> {
  return {
    get(locale) {
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(templates)) {
        result[key] = resolveLocale(locale, value as LocaleSpecific<unknown>);
      }
      return result as T;
    },
  };
}
